import React from 'react';
import PropTypes from 'prop-types';
import { Fab, Tooltip, Box, Skeleton } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useEditMode } from '../../../../contexts/EditModeContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';

/**
 * Compact room menu for Normal Mode
 * Shows a simple "Edit Rooms" FAB button that enters Edit Mode
 * Gated by PROJECT_WRITE permission
 */
const CompactRoomMenu = ({ isDrawingMode, isDeleteMode, disabled = false }) => {
    const { enterEditMode } = useEditMode();
    const { projectId } = useParams();
    const { canProject, isLoading } = usePermissions();

    // When in drawing or delete mode, don't show the button
    if (isDrawingMode || isDeleteMode) {
        return null;
    }

    // Gate: only show if user has PROJECT_WRITE permission
    // Golden rule: show skeleton while loading, hide if no permission
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Skeleton variant="circular" width={40} height={40} />
            </Box>
        );
    }

    if (!canProject(projectId, PERMISSIONS.PROJECT_WRITE)) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Edit Rooms" placement="top">
                <Fab
                    size="small"
                    onClick={enterEditMode}
                    disabled={disabled}
                    aria-label="edit rooms"
                    sx={{
                        boxShadow: { xs: 2, sm: 1 },
                        '&:hover': { boxShadow: { xs: 4, sm: 3 } },
                    }}
                >
                    <EditIcon />
                </Fab>
            </Tooltip>
        </Box>
    );
};

CompactRoomMenu.propTypes = {
    isDrawingMode: PropTypes.bool,
    isDeleteMode: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default CompactRoomMenu;
